import { formatCZK, formatCZKCompact, formatDate, formatTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { resolveImg } from "@/lib/api";
import { Pencil, MapPin } from "lucide-react";

const initials = (n) => ((n || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()) || "?";
const DAY = 86400000;
const dayDiff = (iso) => {
  const d = new Date((iso || "").slice(0, 10) + "T00:00:00");
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / DAY);
};

function Kicker({ children, right }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground">{children}</span>
      {right && <span className="text-xs text-muted-foreground whitespace-nowrap">{right}</span>}
    </div>
  );
}
function Bar({ pct, danger, success }) {
  return (
    <div className="h-2 rounded-full bg-secondary overflow-hidden mt-2">
      <div className={`h-full ${danger ? "bg-destructive" : success ? "bg-success" : "bg-brand"}`}
        style={{ width: `${Math.max(0, Math.min(pct, 100))}%` }} />
    </div>
  );
}

// 4 KPI cards (Čerpání rozpočtu / Termín / Teď na stavbě / Marže).
// Rendered by the «Informace» tab BELOW the tab bar — not in the header.
export function ProjectStatusStrip({ proj, eco, live }) {
  const planned = eco ? (eco.total_costs || 0) + (eco.remaining_budget || 0) : proj.planned_budget || 0;
  const spent = eco?.total_costs || 0;
  const spentPct = planned > 0 ? Math.round((spent / planned) * 100) : 0;
  const marginPct = proj.contract_price ? Math.round(((eco?.margin || 0) / proj.contract_price) * 100) : null;
  const daysLeft = proj.end_date ? dayDiff(proj.end_date) : null;
  const elapsedPct = proj.start_date && proj.end_date
    ? Math.max(0, Math.min(100, Math.round(((Date.now() - +new Date(proj.start_date)) / (+new Date(proj.end_date) - +new Date(proj.start_date))) * 100)))
    : null;
  const clocked = live?.clocked_in || [];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="project-status-strip">
      <div className="bg-card border border-border rounded-[14px] p-4">
        <Kicker right={planned ? `z ${formatCZKCompact(planned)}` : null}>Čerpání rozpočtu</Kicker>
        <div className="text-2xl sm:text-3xl font-extrabold tracking-tight tabular-nums mt-1" title={formatCZK(spent)}>
          {formatCZKCompact(spent)} <span className="text-sm font-bold text-muted-foreground">· {spentPct} %</span>
        </div>
        <Bar pct={spentPct} danger={spentPct > 100} />
      </div>
      <div className="bg-card border border-border rounded-[14px] p-4">
        <Kicker right={elapsedPct != null ? `uplynulo ${elapsedPct} % času` : null}>Termín</Kicker>
        {daysLeft != null ? (
          <>
            <div className={`text-2xl sm:text-3xl font-extrabold tracking-tight tabular-nums mt-1 ${daysLeft < 0 ? "text-destructive" : ""}`}>
              {Math.abs(daysLeft)} <span className="text-sm font-bold text-muted-foreground">{daysLeft < 0 ? "dní po termínu" : "dní do dokončení"}</span>
            </div>
            <Bar pct={elapsedPct ?? 0} danger={daysLeft < 0} success={daysLeft >= 0} />
          </>
        ) : <div className="text-sm text-muted-foreground mt-2">Bez termínu</div>}
      </div>
      <div className="bg-card border border-border rounded-[14px] p-4">
        <Kicker>Teď na stavbě</Kicker>
        {clocked.length ? (
          <>
            <div className="flex items-center mt-1">
              {clocked.slice(0, 4).map((c, i) => (
                <span key={c.user_id} title={`${c.name} · od ${formatTime(c.check_in)}`}
                  className={`w-7 h-7 rounded-full bg-success/15 text-success border-2 border-card inline-flex items-center justify-center text-[10px] font-extrabold ${i > 0 ? "-ml-2" : ""}`}>
                  {initials(c.name)}
                </span>
              ))}
              <span className="text-2xl font-extrabold tabular-nums ml-2">{clocked.length}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1 tabular-nums">
              dnes {String((live?.today_hours || 0).toFixed(1)).replace(".", ",")} h{live?.today_cost != null ? ` · ${formatCZK(live.today_cost)}` : ""}
            </div>
          </>
        ) : <div className="text-sm text-muted-foreground mt-2">Nikdo není přihlášen</div>}
      </div>
      {marginPct != null && (
        <div className="bg-card border border-border rounded-[14px] p-4">
          <Kicker>Marže</Kicker>
          <div className={`text-2xl sm:text-3xl font-extrabold tracking-tight tabular-nums mt-1 ${(eco?.margin || 0) < 0 ? "text-destructive" : ""}`}>
            {marginPct} <span className="text-sm font-bold text-muted-foreground">%</span>
          </div>
          <div className="text-xs text-muted-foreground tabular-nums" title={formatCZK(eco?.margin)}>{formatCZKCompact(eco?.margin || 0)}</div>
        </div>
      )}
    </div>
  );
}

export function ProjectHeader({ proj, manager, statuses, onEdit, onStatusChange }) {
  // 419: další stavbyvedoucí (stavbyvedoucí v týmu zakázky) jako „+N“, jména v bublině
  const deps = (proj.team_users || []).filter((u) => u.role === "MANAGER" && u.id !== proj.site_manager_id);
  const facts = [
    { label: "Investor", value: proj.investor?.name },
    { label: "Stavbyvedoucí", value: proj.site_manager_name || deps[0]?.name, avatar: true,
      more: (proj.site_manager_name ? deps : deps.slice(1)).map((u) => u.name) },
    { label: "Zahájení", value: proj.start_date ? formatDate(proj.start_date) : null },
    { label: "Dokončení", value: proj.end_date ? formatDate(proj.end_date) : null },
  ];

  return (
    <div className="bg-card border border-border rounded-[16px] overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,.05)]" data-testid="project-header">
      {/* Hero */}
      <div className="relative h-[140px] md:h-[190px] bg-secondary">
        {proj.cover_photo && (
          <img src={resolveImg(proj.cover_photo)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,13,11,.72) 0%, rgba(15,13,11,.35) 42%, rgba(15,13,11,0) 70%)" }} />
        <div className="absolute left-5 md:left-6 bottom-4 md:bottom-5 text-white min-w-0 pr-4">
          {proj.code && <div className="text-[11px] font-extrabold uppercase tracking-[.14em] text-white/70">{proj.code}</div>}
          <h1 className="text-[22px] md:text-[34px] font-extrabold tracking-tight leading-tight truncate">{proj.name}</h1>
          {proj.address && <div className="text-sm text-white/80 flex items-center gap-1"><MapPin size={13} /> {proj.address}</div>}
        </div>
        <div className="absolute right-4 top-4 flex items-center gap-2">
          {manager ? (
            <>
              <Button onClick={onEdit} data-testid="edit-project-btn" className="h-[38px] rounded-full bg-white/90 hover:bg-white text-foreground dark:text-black font-bold text-[13px] px-4 border-0">
                <Pencil size={14} /> Upravit
              </Button>
              <Select value={proj.status} onValueChange={onStatusChange}>
                <SelectTrigger className="h-[38px] rounded-full bg-success text-white border-0 font-bold text-[13px] w-auto gap-2 px-4" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </>
          ) : <StatusBadge status={proj.status} />}
        </div>
      </div>
      {/* Fact strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-border/60">
        {facts.map((f) => (
          <div key={f.label} className="px-5 py-4 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground mb-1">{f.label}</div>
            {f.value ? (
              <div className="text-[15px] font-bold truncate flex items-center gap-2">
                {f.avatar && <span className="w-6 h-6 rounded-full bg-brand/15 text-brand inline-flex items-center justify-center text-[10px] font-extrabold shrink-0">{initials(f.value)}</span>}
                <span className="truncate">{f.value}</span>
                {f.more?.length > 0 && <span className="text-muted-foreground font-semibold shrink-0" title={f.more.join(", ")} data-testid="header-more-managers">+{f.more.length}</span>}
              </div>
            ) : manager ? (
              <button onClick={onEdit} className="rounded-full bg-brand/10 border border-brand/25 text-brand px-2.5 py-0.5 text-xs font-bold">+ Doplnit</button>
            ) : <span className="text-[15px] font-bold text-muted-foreground">—</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
